import { computed, inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ParamMap, Router } from "@angular/router";

import {
  BOARD_VIEW_MODE,
  BoardViewMode,
} from "@juristech/contracts/orders/board.types";

export type DashboardPaymentStatus = "paid" | "unpaid";

const PAYMENT_STATUS_VALUES: ReadonlySet<DashboardPaymentStatus> = new Set([
  "paid",
  "unpaid",
]);

export type DashboardCompletedFilter = "all" | "open" | "completed";

const COMPLETED_FILTER_VALUES: ReadonlySet<DashboardCompletedFilter> = new Set([
  "all",
  "open",
  "completed",
]);

const DEFAULT_COMPLETED_FILTER: DashboardCompletedFilter = "open";

/** Valid board lane selectors (slice-09 `BoardViewMode`, the single source of truth). */
const VIEW_MODE_VALUES: ReadonlySet<BoardViewMode> = new Set([
  BOARD_VIEW_MODE.Urgency,
  BOARD_VIEW_MODE.Payment,
]);

/** The board defaults to the urgency view (C1 — today's behaviour for every bookmark). */
const DEFAULT_VIEW_MODE: BoardViewMode = BOARD_VIEW_MODE.Urgency;

/** Sentinel `owner` value selecting orders with no owner. Mirrors the API's `owner=unassigned`. */
export const OWNER_UNASSIGNED = "unassigned" as const;

/**
 * A single board owner-filter token: a user id (My orders / a teammate) or the
 * {@link OWNER_UNASSIGNED} sentinel (Unassigned).
 */
export type DashboardOwnerToken = number | typeof OWNER_UNASSIGNED;

/**
 * Board owner filter — a multi-select list of {@link DashboardOwnerToken}. The
 * board narrows to orders owned by ANY token in the list; `'unassigned'` may be
 * mixed with user ids. An empty list means "Anyone" (no owner filter). Serialized
 * to `?owner=` as a comma-joined list (e.g. `4088,unassigned`), mirroring
 * `businessUnitIds`.
 */
export type DashboardOwnerFilter = DashboardOwnerToken[];

export type DashboardFilterParam =
  | "searchText"
  | "businessUnitIds"
  | "paymentStatus"
  | "companyId"
  | "stateId"
  | "countyIds"
  | "dateFrom"
  | "dateTo"
  | "orderStatusId"
  | "completed"
  | "owner"
  | "viewMode";

const STORAGE_KEY = "admin-web:dashboard-filters";

const FILTER_PARAM_KEYS = [
  "searchText",
  "businessUnitIds",
  "paymentStatus",
  "companyId",
  "stateId",
  "countyIds",
  "dateFrom",
  "dateTo",
  "orderStatusId",
  "completed",
  "owner",
  "viewMode",
] as const satisfies readonly DashboardFilterParam[];

/** Exposed only for tests — production code reads/writes via the store API. */
export const STORAGE_KEY_FOR_TESTING = STORAGE_KEY;

/**
 * URL-bound signal store for the admin Orders dashboard filter set. Both the table
 * (`/dashboard`) and the kanban board (`/dashboard/board`) read from and write to the
 * same query-string contract via this store. Bookmarking and deep-linking work because
 * filter state lives in the URL — the store is a thin reactive view over `route.queryParamMap`.
 */
@Injectable({ providedIn: "root" })
export class DashboardFiltersStore {
  private readonly router = inject(Router);

  private readonly paramMap = toSignal(
    this.router.routerState.root.queryParamMap,
    {
      initialValue: this.router.routerState.root.snapshot
        .queryParamMap as ParamMap,
    },
  );

  readonly searchText = computed<string>(
    () => this.paramMap().get("searchText") ?? "",
  );

  readonly orderStatusId = computed<number | null>(() =>
    parseIntOrNull(this.paramMap().get("orderStatusId")),
  );

  readonly companyId = computed<number | null>(() =>
    parseIntOrNull(this.paramMap().get("companyId")),
  );

  readonly businessUnitIds = computed<number[]>(() => {
    const raw = this.paramMap().get("businessUnitIds");
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  });

  /** Property-state filter (`[Location].[States].Id`), or `null` when unset. */
  readonly stateId = computed<number | null>(() =>
    parseIntOrNull(this.paramMap().get("stateId")),
  );

  /**
   * Property-county filter (`[Location].[Counties].Id` multi-select), serialized
   * to `?countyIds=` as a comma-joined list. Only meaningful while {@link stateId}
   * is set — {@link setStateId} clears it on every state change (cascade).
   */
  readonly countyIds = computed<number[]>(() => {
    const raw = this.paramMap().get("countyIds");
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  });

  readonly paymentStatus = computed<DashboardPaymentStatus | null>(() => {
    const raw = this.paramMap().get("paymentStatus");
    return raw && PAYMENT_STATUS_VALUES.has(raw as DashboardPaymentStatus)
      ? (raw as DashboardPaymentStatus)
      : null;
  });

  readonly dateFrom = computed<Date | null>(() =>
    parseDateOrNull(this.paramMap().get("dateFrom")),
  );

  readonly dateTo = computed<Date | null>(() =>
    parseDateOrNull(this.paramMap().get("dateTo")),
  );

  readonly completed = computed<DashboardCompletedFilter>(() => {
    const raw = this.paramMap().get("completed");
    return raw && COMPLETED_FILTER_VALUES.has(raw as DashboardCompletedFilter)
      ? (raw as DashboardCompletedFilter)
      : DEFAULT_COMPLETED_FILTER;
  });

  readonly owner = computed<DashboardOwnerFilter>(() => {
    const raw = this.paramMap().get("owner");
    if (!raw) return [];
    return raw
      .split(",")
      .map((token) => parseOwnerTokenOrNull(token))
      .filter((token): token is DashboardOwnerToken => token !== null);
  });

  /**
   * Board lane selector. Defaults to `'urgency'` (today's SLA board) when the
   * `?viewMode=` param is absent or unrecognized — the payment view is strictly
   * opt-in via the URL, so existing bookmarks keep their behaviour (C1).
   */
  readonly viewMode = computed<BoardViewMode>(() => {
    const raw = this.paramMap().get("viewMode");
    return raw && VIEW_MODE_VALUES.has(raw as BoardViewMode)
      ? (raw as BoardViewMode)
      : DEFAULT_VIEW_MODE;
  });

  setSearchText(value: string): Promise<boolean> {
    return this.merge({ searchText: value || null });
  }

  setOrderStatusId(value: number | null): Promise<boolean> {
    return this.merge({ orderStatusId: value !== null ? String(value) : null });
  }

  setCompanyId(value: number | null): Promise<boolean> {
    return this.merge({ companyId: value !== null ? String(value) : null });
  }

  setBusinessUnitIds(value: number[]): Promise<boolean> {
    return this.merge({
      businessUnitIds: value.length > 0 ? value.join(",") : null,
    });
  }

  /**
   * Selects the property-state filter. Every state change — including clearing
   * it — also clears {@link countyIds} in the same navigation: counties belong
   * to a state, so a stale county selection must never survive a state switch.
   */
  setStateId(value: number | null): Promise<boolean> {
    return this.merge({
      stateId: value !== null ? String(value) : null,
      countyIds: null,
    });
  }

  setCountyIds(value: number[]): Promise<boolean> {
    return this.merge({
      countyIds: value.length > 0 ? value.join(",") : null,
    });
  }

  setPaymentStatus(value: DashboardPaymentStatus | null): Promise<boolean> {
    return this.merge({ paymentStatus: value });
  }

  setDateFrom(value: Date | null): Promise<boolean> {
    return this.merge({ dateFrom: value ? value.toISOString() : null });
  }

  setDateTo(value: Date | null): Promise<boolean> {
    return this.merge({ dateTo: value ? value.toISOString() : null });
  }

  setCompleted(value: DashboardCompletedFilter): Promise<boolean> {
    return this.merge({
      completed: value === DEFAULT_COMPLETED_FILTER ? null : value,
    });
  }

  setOwner(value: DashboardOwnerFilter): Promise<boolean> {
    return this.merge({
      owner:
        value.length > 0 ? value.map((token) => String(token)).join(",") : null,
    });
  }

  /**
   * Selects the board view. Writes `null` for the default (`urgency`) so the
   * query string stays clean for non-switchers; `payment` is persisted.
   */
  setViewMode(value: BoardViewMode): Promise<boolean> {
    return this.merge({ viewMode: value === DEFAULT_VIEW_MODE ? null : value });
  }

  /**
   * Removes every bar-owned filter param at once (single navigate call). Two
   * params are deliberately preserved: the stat-card `?orderStatusId=` filter
   * (status is not a bar control — it is cleared only by its own card toggle
   * or the status-filter chip) and `?viewMode=` (the lane grouping is a view
   * selector, not a filter — Clear must not bounce a payment-view user back
   * to the urgency board; owner decision 2026-07-24).
   */
  clear(): Promise<boolean> {
    const cleared: Record<
      Exclude<DashboardFilterParam, "orderStatusId" | "viewMode">,
      null
    > = {
      searchText: null,
      businessUnitIds: null,
      paymentStatus: null,
      companyId: null,
      stateId: null,
      countyIds: null,
      dateFrom: null,
      dateTo: null,
      completed: null,
      owner: null,
    };
    return this.merge(cleared);
  }

  /**
   * Restores filter params from sessionStorage when the URL has none. Caller MUST await
   * the returned Promise — the dashboard's auto-"New" override in `loadStats` checks the
   * store's signals, which only reflect the restored values once the navigation Promise
   * resolves.
   *
   * @returns `true` when storage was applied, `false` when URL already has filters or
   *   storage is empty.
   */
  async hydrateFromStorage(): Promise<boolean> {
    if (typeof sessionStorage === "undefined") return false;

    const map = this.paramMap();
    const hasUrlFilters = FILTER_PARAM_KEYS.some((k) => map.has(k));
    if (hasUrlFilters) return false;

    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const stored: Partial<Record<DashboardFilterParam, string>> =
      JSON.parse(raw);
    if (Object.keys(stored).length === 0) return false;

    await this.router.navigate([], {
      queryParams: stored,
      queryParamsHandling: "merge",
    });
    return true;
  }

  /**
   * Merges the given params into the URL (and sessionStorage). Returns the
   * router's navigation promise so callers that must read the store's signals
   * immediately after a write (e.g. the shared filter bar's `filtersChanged`
   * emission) can await the URL actually reflecting the new values.
   */
  private merge(
    params: Partial<Record<DashboardFilterParam, string | null>>,
  ): Promise<boolean> {
    this.persistMerge(params);
    return this.router.navigate([], {
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private persistMerge(
    params: Partial<Record<DashboardFilterParam, string | null>>,
  ): void {
    if (typeof sessionStorage === "undefined") return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const current: Partial<Record<DashboardFilterParam, string>> = raw
      ? JSON.parse(raw)
      : {};
    for (const [key, value] of Object.entries(params) as [
      DashboardFilterParam,
      string | null,
    ][]) {
      if (value === null) delete current[key];
      else current[key] = value;
    }
    if (Object.keys(current).length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    }
  }
}

/**
 * Order-sensitive value equality for two owner-filter lists. The store's
 * `owner` computed returns a fresh array reference on every URL change, so
 * hosts reloading off `toObservable(store.owner)` dedupe with this to avoid
 * spurious refetches when an unrelated param changes.
 */
export function sameOwnerFilter(
  a: readonly DashboardOwnerToken[],
  b: readonly DashboardOwnerToken[],
): boolean {
  return a.length === b.length && a.every((token, i) => token === b[i]);
}

/**
 * Narrows one `?owner=` token to a {@link DashboardOwnerToken}, or `null` to drop
 * it. The {@link OWNER_UNASSIGNED} sentinel passes through; otherwise the trimmed
 * token must be all digits and a positive safe integer. Requiring the entire
 * (trimmed) token to be digits mirrors the API's `^\d+$` guard (`parsePositiveInt`
 * in orders.controller.ts): bare `parseInt` would coerce `"5abc"` to `5` and
 * `"12.5"` to `12`, silently filtering by a half-parsed id while the API rejects
 * the same value.
 */
function parseOwnerTokenOrNull(raw: string): DashboardOwnerToken | null {
  const token = raw.trim();
  if (token === OWNER_UNASSIGNED) return OWNER_UNASSIGNED;
  if (!/^\d+$/.test(token)) return null;
  const value = parseInt(token, 10);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

function parseIntOrNull(raw: string | null): number | null {
  if (!raw) return null;
  const value = parseInt(raw, 10);
  return Number.isNaN(value) ? null : value;
}

function parseDateOrNull(raw: string | null): Date | null {
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}
